import {
  LOGO_FIT_RATIO,
  LOGO_MAX_BYTES,
  LOGO_ZOOM_MAX,
  LOGO_ZOOM_MIN,
  type LogoFraming,
  type LogoRejectionCode,
  ensureSvgViewBox,
} from '@mss_creator/shared';
import { blobToText } from './data-url';

/**
 * The geometry of a company logo in its circle (issue #20, planche 06). The
 * circle is the unit: the image is fitted in a square box centred on the circle
 * whose side is `zoom` times `LOGO_FIT_RATIO` of the circle's diameter, and
 * `offsetX` / `offsetY` move the box's centre away from the circle's, in
 * fractions of the box. Everything is a percentage of the circle, so the same
 * framing draws identically at 64, 80 and 240 px, and the browser's
 * `object-fit: contain` does the rest of the fitting — the drawing needs no
 * knowledge of the image's own size.
 */

/** The box the image is fitted in, as a position and a side, in percent of the circle. */
export interface LogoBox {
  left: number;
  top: number;
  size: number;
}

export function logoBox(framing: LogoFraming): LogoBox {
  const size = framing.zoom * LOGO_FIT_RATIO * 100;
  return {
    size,
    left: 50 + framing.offsetX * size - size / 2,
    top: 50 + framing.offsetY * size - size / 2,
  };
}

/**
 * How far the box's centre may stray from the circle's, per axis, in fractions
 * of the box, while the circle stays covered by the image. `aspect` is the
 * image's width over its height. An axis on which the image, once fitted, is
 * smaller than the circle cannot cover it at all: it stays centred (0).
 */
export function maxLogoOffsets(zoom: number, aspect: number): { x: number; y: number } {
  const box = zoom * LOGO_FIT_RATIO;
  const imageWidth = box * Math.min(1, aspect);
  const imageHeight = box * Math.min(1, 1 / aspect);
  return {
    x: Math.max(0, (imageWidth - 1) / 2) / box,
    y: Math.max(0, (imageHeight - 1) / 2) / box,
  };
}

function clamp(value: number, min: number, max: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  // Never a negative zero: an axis that cannot move reads 0, whichever way it was pushed.
  return clamped === 0 ? 0 : clamped;
}

/** The framing with its zoom inside 1 to 4 and its offsets inside what keeps the circle covered. */
export function clampLogoFraming(framing: LogoFraming, aspect: number): LogoFraming {
  const zoom = clamp(framing.zoom, LOGO_ZOOM_MIN, LOGO_ZOOM_MAX);
  const max = maxLogoOffsets(zoom, aspect);
  return {
    zoom,
    offsetX: clamp(framing.offsetX, -max.x, max.x),
    offsetY: clamp(framing.offsetY, -max.y, max.y),
  };
}

/** A framing differs from another only past the rounding the stored values go through. */
export function sameLogoFraming(a: LogoFraming, b: LogoFraming): boolean {
  const close = (x: number, y: number) => Math.abs(x - y) < 1e-6;
  return close(a.zoom, b.zoom) && close(a.offsetX, b.offsetX) && close(a.offsetY, b.offsetY);
}

/** « 3,2x »: the zoom as the dialog writes it (French decimal comma, one digit at most). */
export function formatZoom(zoom: number): string {
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(zoom)}x`;
}

const FILE_EXTENSION_TYPES: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
};

const ACCEPTED_TYPES: ReadonlySet<string> = new Set(Object.values(FILE_EXTENSION_TYPES));

/** A chosen file's media type: the browser's, or the extension's when the browser gives none. */
export function logoFileType(file: File): string {
  if (file.type !== '') return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return FILE_EXTENSION_TYPES[extension] ?? '';
}

/**
 * The first reason a chosen file cannot be a logo, judged in the browser before
 * anything is sent: its type (from the browser, or from the extension when the
 * browser gives none) and its size. The server then recognises the content
 * itself and has the last word.
 */
export function logoFileProblem(file: File): LogoRejectionCode | null {
  if (!ACCEPTED_TYPES.has(logoFileType(file))) {
    return 'LOGO_FORMAT_UNSUPPORTED';
  }
  if (file.size > LOGO_MAX_BYTES) {
    return 'LOGO_TOO_LARGE';
  }
  return null;
}

/** « 14 Ko », « 1,2 Mo »: a file's size the way the dialog states it. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
  }
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} Mo`;
}

/**
 * What a chosen file becomes before it is previewed or sent (issue #22). An SVG
 * without a `viewBox` never scales in the circle, so one that states its
 * `width` and `height` is given the `viewBox` they call for, and the dialog
 * previews, and later uploads, that file; one that states no size at all is
 * refused, with the same code the server answers. Any other file, or an SVG that
 * is already fine, comes back as it is. The server does the same on its side
 * and has the last word.
 */
export async function fitLogoFile(
  file: File,
): Promise<{ file: File } | { problem: LogoRejectionCode }> {
  if (logoFileType(file) !== 'image/svg+xml') return { file };
  const sizing = ensureSvgViewBox(await blobToText(file));
  if (sizing.status === 'no-size') return { problem: 'LOGO_SVG_NO_SIZE' };
  if (sizing.status === 'unchanged') return { file };
  return { file: new File([sizing.svg], file.name, { type: logoFileType(file) }) };
}
